import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "semantic-ui-react";
import Tab from "../../components/Tab";
import { Search } from "react-feather";
import { FORMS, MODULES } from "../../const";
import Button from "../../components/input/Button";
import TaxSearch from "../../components/finance/TaxSearch";
import LoadingSpinner from "../../components/LoadingSpinner";
import Grid from "../../components/Grid";
import {
  DateCellRenderer,
  ResizableScrollingTextRenderer,
} from "../../components/renderer/StyleRenderer";
import ButtonRenderer from "../../components/renderer/ButtonRenderer";
import Dropdown from "../../components/input/Dropdown";
import {
  getBulkDropdownData,
  getOtherRefDetails,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import Input from "../../components/input/Input";
import SearchDropdown from "../../components/input/SearchDropdown";
import {
  getPerformanceSummary,
  performanceMonitorByRequest,
  searchPerformanceMonitor,
} from "../../api/performanceMonitorApi";
import InfoList from "../../components/InfoList";

function PerformanceMonitoring() {
  const [tab, setTab] = useState(1);
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSearchFields, setResetSearchFields] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [dateType, setDateType] = useState(1);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customer, setCustomer] = useState([]);
  const [shipper, setShipper] = useState([]);
  const [vehicleNo, setVehicleNo] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [requestCode, setRequestCode] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");

  const [dateTypeOptions, setDateTypeOptions] = useState([]);

  const [searchResult, setSearchResult] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // useRef for sideDrawer or Modals
  const reportRef = useRef(null);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const { date_types: dateTypeData } = await getBulkDropdownData([
        { key: "date_types" },
      ]);

      setDateTypeOptions(dateTypeData);
      setIsLoading(false);
    })();
  }, []);

  const handleReset = async () => {
    setResetSearchFields(true);
    setDateType(1);
    setFromDate("");
    setToDate("");
    setCustomer([]);
    setShipper([]);
    setVehicleNo(null);
    setReferenceNo(null);
    setRequestCode(null);
    setPickupLocation(null);
    setDeliveryLocation(null);
    setSummaryData(null);
    setSearchClicked(false);
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setSearchClicked(true);

    const payload = {
      reqCode: requestCode || null,
      referenceNo: referenceNo || null,
      vehicle: vehicleNo || null,
      pickup: pickupLocation?.retId || null,
      delivery: deliveryLocation?.retId || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
      dateType: dateType || null,
      customerId: customer.length > 0 ? customer[0] : null,
      plantId: shipper.length > 0 ? shipper[0] : null,
    };

    try {
      const search = await searchPerformanceMonitor(payload);
      console.log("Search result:", search);
      setSearchResult(search);

      const searchSanitized = (search || []).filter(Boolean);

      const jobIds = [
        ...new Set(searchSanitized.map((item) => item.jobId).filter(Boolean)),
      ];
      const requestIds = [
        ...new Set(searchSanitized.map((item) => item.reqId).filter(Boolean)),
      ];
      const chhIds = [
        ...new Set(searchSanitized.map((item) => item.chhId).filter(Boolean)),
      ];

      // ✅ Build payload for summary
      const summaryPayload = {
        jobIds,
        requestIds,
        chhIds,
      };

      // ✅ Call summary endpoint
      const summary = await getPerformanceSummary(summaryPayload);
      console.log("Performance summary:", summary);
      setSummaryData(summary); // assuming you have a state for this
    } catch (e) {
      console.error("Failed to fetch performance monitor data or summary", e);
    }

    setIsLoading(false);
  };

  const valueFormatter = (params) => {
    const value = params.value;
    return value === null || value === undefined || value === ""
      ? "N/A"
      : value;
  };

  const performanceGridConfig = [
    {
      field: "reqCode",
      headerName: "Request ID",
      valueFormatter,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    { field: "jobCode", headerName: "Job No", valueFormatter },
    {
      field: "reqCreatedDate",
      headerName: "Created Date",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    { field: "reqCreatedBy", headerName: "Created By", valueFormatter },
    {
      field: "customerName",
      headerName: "Customer",
      cellRenderer: ResizableScrollingTextRenderer,
      valueFormatter,
    },
    { field: "vehicle", headerName: "Vehicle", valueFormatter },
    { field: "driver", headerName: "Driver", valueFormatter },
    { field: "finConf", headerName: "Finance Confirmation", valueFormatter },
    { field: "jobConf", headerName: "Job Confirmation Action", valueFormatter },
    {
      field: "ontimePlacement",
      headerName: "On Time Placement",
      valueFormatter,
    },
    { field: "attName", headerName: "Attended By", valueFormatter },
    { field: "ontimeDelivery", headerName: "On Time Delivery", valueFormatter },
    {
      field: "sameDayDelivery",
      headerName: "Same day Delivery",
      valueFormatter,
    },
    {
      field: "spentAtPickUpPercent",
      headerName: "Spent at PP%",
      valueFormatter,
    },
    {
      field: "spentAtDeliveryPercent",
      headerName: "Spent at DP%",
      valueFormatter,
    },
    { field: "lateBooking", headerName: "Late Booking", valueFormatter },
    {
      field: "transitTimePercent",
      headerName: "Transit time%",
      valueFormatter,
    },
    {
      field: "placementStatus",
      headerName: "Placement Status",
      valueFormatter,
    },
    {
      field: "arrAtPickup",
      headerName: "Arrived at Pickup",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    { field: "spentAtPickUp", headerName: "Spent at PP", valueFormatter },
    {
      field: "depFromPickup",
      headerName: "Departed from Pickup",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    { field: "transitTimeText", headerName: "Transit Time", valueFormatter },
    {
      field: "arrAtDel",
      headerName: "Arrived at Delivery",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    {
      field: "depFromDel",
      headerName: "Departure from Delivery",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    { field: "spentAtDelivery", headerName: "Spent at DP", valueFormatter },
    {
      field: "billCustName",
      headerName: "Billing Customer",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      field: "shipperName",
      headerName: "Shipper/Plant",
      cellRenderer: ResizableScrollingTextRenderer,
      valueFormatter,
    },
    {
      field: "pickup",
      headerName: "Pickup",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      field: "requestedPickupArrTime",
      headerName: "Requested Arrival Time",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    {
      field: "plannedPickupDispatchTime",
      headerName: "Planned Dispatch Time",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    {
      field: "via",
      headerName: "Via Locations",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      field: "delivery",
      headerName: "Delivery",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      field: "requestedDeliveryArrTime",
      headerName: "Requested Delivery Time",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    {
      field: "reqShipmentTypeValue",
      headerName: "Shipment Type",
      valueFormatter,
    },
    {
      field: "consignmentTypeValue",
      headerName: "Consignment Type",
      valueFormatter,
    },
    {
      field: "consignees",
      headerName: "Consignee",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    { field: "reqVehType", headerName: "Vehicle Type", valueFormatter },
    { field: "reqCbm", headerName: "CBM/Volume", valueFormatter },
    {
      field: "containerTypes",
      headerName: "Container Type",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    { field: "reqRemarks", headerName: "Creator Remarks", valueFormatter },
    { field: "appName", headerName: "Approvals", valueFormatter },
    {
      field: "stkName",
      headerName: "Transporter",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      field: "jobArrivalConfDate",
      headerName: "Arrival Confirmation",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    {
      field: "jobRunningConfirmationDate",
      headerName: "Running sheet confirmation",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    {
      field: "reqDeliveryCutoff",
      headerName: "Yard/Flight Cutoff Delivery Time",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    }, //requested delivery time
    {
      field: "estDurationText",
      headerName: "Estimated Duration",
      valueFormatter,
    },
    {
      field: "fullDurationText",
      headerName: "Actual Duration",
      valueFormatter,
    },
    { field: "jobKmActual", headerName: "Cargo In Hand KM", valueFormatter },
    { field: "jobChargeableKm", headerName: "Chargeble KM", valueFormatter },
    { field: "kmConf", headerName: "KM Confirmation", valueFormatter },
    { field: "jobCbm", headerName: "CBM", valueFormatter },
    { field: "reqPackages", headerName: "Packages", valueFormatter },
    { field: "containerNos", headerName: "Container Number", valueFormatter },
    { field: "reqContactPerson", headerName: "Contact Person", valueFormatter },
    { field: "reqContactNumber", headerName: "Contact Number", valueFormatter },
    { field: "depValue", headerName: "Department", valueFormatter },
    { field: "reqPono", headerName: "Reference PO No", valueFormatter },
    {
      field: "jobPonumber",
      headerName: "Security/Serial Number",
      valueFormatter,
    },
    {
      field: "reqOther",
      headerName: "Other Requests",
      valueFormatter,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    { field: "appName", headerName: "Approved By", valueFormatter },
    {
      field: "reqApprovedDate",
      headerName: "Approved Date",
      valueFormatter,
      cellRenderer: DateCellRenderer,
    },
    { field: "jobOperationRemark", headerName: "Remarks", valueFormatter },
    {
      field: "reqCarbonFactor",
      headerName: "Carbon Footprint",
      valueFormatter,
    },
    {
      field: "hasReqDocs",
      headerName: "UPLOAD DOCUMENT STATUS",
      valueFormatter,
    },
    { field: "statusName", headerName: "Current Status", valueFormatter },
  ];

  const formatKey = (key) => {
    if (!key) return "";

    // Remove the "js" prefix if present
    const clean = key.replace(/^js/, "");

    // Add spaces before capital letters and capitalize the first letter
    return clean
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <>
      <Card style={{ width: "100%" }}>
        <CardContent>
          <div
            className="ui attached tabular"
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {/* <div className="ui tabular menu top attached">
              <Tab
                Icon={Search}
                onClick={() => {
                  setTab(1);
                }}
                active={tab === 1}
                label="Search"
              />
            </div> */}
            {/*<Button*/}
            {/*    template="drawerOpen"*/}
            {/*    // onClick={handleDrawerOpen}*/}
            {/*    editMode={false}*/}
            {/*/>*/}
          </div>
          <div
            //className="ui bottom attached active tab segment"
            style={{ display: tab === 1 ? "block" : "none" }}
          >
            <div
              className="ui styled accordion"
              style={{ width: "100%", marginBottom: "10px" }}
            >
              <div
                className={`title ${activeAccordion === 1 ? "active" : ""}`}
                // onClick={() => handleAccordionClick(1)}
              >
                <div>
                  Search
                  <Search size={14} style={{ marginLeft: "10px" }} />
                  <i className="dropdown icon"></i>
                </div>
              </div>
              <div
                className={`content ${activeAccordion === 1 ? "active" : ""}`}
              >
                {/*    the performance search   */}
                <form className="ui form">
                  <h4 className="ui dividing header">
                    Performance Monitor Search
                  </h4>
                  <div
                    className="four fields"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Dropdown
                      required={true}
                      options={dateTypeOptions}
                      label="Date Type"
                      placeholder="Date Type"
                      style={{ height: "20px" }}
                      value={dateType}
                      onChange={(e, data) => {
                        setDateType(data.value);
                      }}
                      clearable={false}
                    />
                    <Input
                      label="From Date"
                      type="date"
                      name="fromDate"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                      }}
                    />
                    <Input
                      label={"To Date"}
                      type="date"
                      name="toDate"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                      }}
                    />
                    <SearchDropdown
                      key={1}
                      editMode={false}
                      reset={resetSearchFields}
                      setReset={setResetSearchFields}
                      // occupySpace={false}
                      // multiple={true}
                      // multiValueRemoveHandler={(key) => {
                      //     setCustomer((prev) =>
                      //         prev.filter((item) => item !== parseInt(key)),
                      //     );
                      // }}
                      onClear={() => setCustomer([])}
                      onResultSelect={(data) =>
                        setCustomer((prev) => [...prev, data])
                      }
                      api={getOtherRefSearchDropdown}
                      apiDependency={{
                        type: "stakeholder_type",
                        value: "Customer",
                      }}
                      label="Customer"
                    />
                    <SearchDropdown
                      key={2}
                      editMode={false}
                      reset={resetSearchFields}
                      setReset={setResetSearchFields}
                      // occupySpace={false}
                      // multiple={true}
                      // multiValueRemoveHandler={(key) => {
                      //     setShipper((prev) =>
                      //         prev.filter((item) => item !== parseInt(key)),
                      //     );
                      // }}
                      onClear={() => setShipper([])}
                      onResultSelect={(data) =>
                        setShipper((prev) => [...prev, data])
                      }
                      api={getOtherRefSearchDropdown}
                      apiDependency={{
                        type: "stakeholder_type",
                        value: "Shipper",
                        param2: customer.length > 0 ? customer.join(",") : "",
                      }}
                      label="Shipper/Plant"
                    />
                  </div>
                  <div
                    className="four fields"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    {/*<Input*/}
                    {/*    label="Vehicle No"*/}
                    {/*    type="text"*/}
                    {/*    name="vehicleNo"*/}
                    {/*    value={vehicleNo}*/}
                    {/*    onChange={(e) => {*/}
                    {/*        setVehicleNo(e.target.value);*/}
                    {/*    }}*/}
                    {/*/>*/}
                    <SearchDropdown
                      key={1}
                      label={"Vehicle"}
                      clearable={true}
                      onClear={() => setVehicleNo("")}
                      reset={resetSearchFields}
                      setReset={setResetSearchFields}
                      onResultSelect={(data) => setVehicleNo(data)}
                      api={getOtherRefSearchDropdown}
                      apiDependency={{
                        type: "vehicle",
                        value: "",
                      }}
                    />
                    {/*<Input*/}
                    {/*    label="Reference No"*/}
                    {/*    type="text"*/}
                    {/*    name="referenceNo"*/}
                    {/*    value={referenceNo}*/}
                    {/*    onChange={(e) => {*/}
                    {/*        setReferenceNo(e.target.value);*/}
                    {/*    }}*/}
                    {/*/>*/}
                    <Input
                      label="Request Code"
                      type="text"
                      name="requestCode"
                      value={requestCode}
                      onChange={(e) => {
                        setRequestCode(e.target.value);
                      }}
                    />
                    {/*<Input*/}
                    {/*    label="Pickup Location"*/}
                    {/*    type="text"*/}
                    {/*    name="pickupLocation"*/}
                    {/*    value={pickupLocation}*/}
                    {/*    onChange={(e) => {*/}
                    {/*        setPickupLocation(e.target.value);*/}
                    {/*    }}*/}
                    {/*/>*/}

                    <SearchDropdown
                      clearable={true}
                      field={"pickupLocation"}
                      reset={resetSearchFields}
                      setReset={setResetSearchFields}
                      api={getOtherRefDetails}
                      onClear={() => setPickupLocation("")}
                      label={"Pickup Location"}
                      threshold={3}
                      apiDependency={{
                        type: "location",
                        value: "",
                      }}
                      returnFullObject={true}
                      onResultSelect={(data) => setPickupLocation(data)}
                      fieldInfo={{
                        tableName: "masterdata.tms_m_locations",
                        primaryKey: "loc_id",
                        fieldName: "loc_name",
                      }}
                    />
                    <SearchDropdown
                      clearable={true}
                      field={"deliveryLocation"}
                      reset={resetSearchFields}
                      setReset={setResetSearchFields}
                      api={getOtherRefDetails}
                      onClear={() => setDeliveryLocation("")}
                      label={"Delivery Location"}
                      threshold={3}
                      apiDependency={{
                        type: "location",
                        value: "",
                      }}
                      returnFullObject={true}
                      onResultSelect={(data) => setDeliveryLocation(data)}
                      fieldInfo={{
                        tableName: "masterdata.tms_m_locations",
                        primaryKey: "loc_id",
                        fieldName: "loc_name",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className="field" style={{ width: "100%" }}>
                      <Button template="search" onClick={handleSearch} />
                      <Button template="searchReset" onClick={handleReset} />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          {/*  the grid  */}
          {searchClicked &&
            (isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <Grid
                  heading="Performance Monitor"
                  downloadParam={""}
                  downloadAction="Performance Excel Download"
                  content={searchResult}
                  config={performanceGridConfig}
                  pagination={true}
                  multiRowSelection={true}
                  showDownloadButton={true}
                  downloadOnlySelected={true}
                  reportName="performance_monitor_report.csv"
                />
                <InfoList
                  content={
                    summaryData
                      ? Object.entries(summaryData)
                          .filter(
                            ([key]) =>
                              key.toLowerCase() !== "id" &&
                              ![
                                "jsDemCh", // Demurrage Charges
                                "jsNiAllCh", // NI All Charges
                                "jsManagementCh", // Management Charges
                                "jsTransportCh", // Transport Charges
                                "jsCrDebAmt", // Credit/Debit Amount
                                "jsOtherCh",
                                "jsTotInvAmt",
                                "jsTotCh",
                              ].includes(key)
                          )
                          .map(([key, value]) => {
                            // Map keys to friendly labels
                            const labelMap = {
                              jsCompletedJobs: "Completed Jobs",
                              jsOnTimePlacement: "On Time Placement (%)",
                              jsOnTimeDelivery: "On Time Delivery (%)",
                              jsLateBooking: "Late Booking (%)",
                              jsSameDayDelivery: "Same Day Delivery (%)",
                              jsTransitTime: "Transit Time",
                              jsSpentAtPickup: "Spent at Pickup",
                              jsSpentAtPickupAvg: "Avg Spent at Pickup",
                              jsSpentAtPickupMtFiveh: "Spent at Pickup > 5h",
                              jsOnTimeBooking: "On Time Booking",
                              jsConfirmedJobs: "Confirmed Jobs",
                              jsDisputedJobs: "Disputed Jobs",
                              jsResolvedJobs: "Resolved Jobs",
                              jsSpentAtDelivery: "Spent at Delivery",
                              jsSpentAtDeliveryAvg: "Avg Spent at Delivery",
                              jsSpentAtDeliveryMtFiveh:
                                "Spent at Delivery > 5h",
                              jsOtPlForOtBooking:
                                "On Time Placement for On Time Booking",
                              jsFinComJobs: "Finance Completed Jobs",
                              jsFinDisJob: "Finance Disputed Jobs",
                              jsFinResJob: "Finance Resolved Jobs",
                            };

                            // Format percentage values
                            const percentFields = [
                              "jsOnTimePlacement",
                              "jsOnTimeDelivery",
                              "jsLateBooking",
                              "jsSameDayDelivery",
                            ];
                            let displayValue = value ?? "N/A";
                            if (
                              percentFields.includes(key) &&
                              value !== null &&
                              value !== undefined
                            ) {
                              displayValue = `${parseFloat(value).toFixed(2)}%`;
                            }

                            return {
                              title: labelMap[key] || formatKey(key),
                              value: displayValue,
                            };
                          })
                      : []
                  }
                  title="Performance Monitor Summary"
                />
              </>
            ))}
        </CardContent>
      </Card>
    </>
  );
}

export default PerformanceMonitoring;
